import { NoAccounts } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Container,
  Chip,
  Stack
} from "@mui/material";
import React from "react";
import { List } from "../../../@types/list";
import { AnsweringClient } from "../../../clients/answeringClient";
import EmptyState from "../../../SharedComponents/EmptyState/EmptyState";
import { Pagination } from "../../../SharedComponents/Pagination";
import { SearchInput } from "../../../SharedComponents/SearchInput";
import { withNavigationHooks } from "../../../SharedComponents/withNavigationHooks";
import { calculateTotalPages } from "../../../utils/pagination";
import {
  ResellersController,
  ResellersControllerDelegate,
} from "../../controllers/ResellersController";
import { IResellerSearchResult } from "../../types";
import {
  DemoteResellerModal,
  DemoteResellerModalDelegate,
} from "../modals/DemoteResellerModal/DemoteResellerModal";
import ResellerRow from "./ResellerRow";

interface State {
  isLoading: boolean;
  resellers: IResellerSearchResult[];
  filters: { filter: string; page: number; limit: number; total: number };
  isDemoteModalOpen: boolean;
  userToDemote?: { uid: string; name: string };
  demotionImpactData: {
    clientsCount: number;
    activeLicensesCount: number;
  } | null;
  isActionLoading: boolean;
  snackbar: { open: boolean; message: string; severity: "success" | "error" };
}

class ResellersTab
  extends React.Component<{}, State>
  implements ResellersControllerDelegate, DemoteResellerModalDelegate {
  private controller: ResellersController;
  private answeringClient = new AnsweringClient();

  constructor(props: {}) {
    super(props);
    this.controller = new ResellersController(this);
    this.state = {
      isLoading: true,
      resellers: [],
      filters: { filter: "", page: 1, limit: 10, total: 0 },
      isDemoteModalOpen: false,
      userToDemote: undefined,
      demotionImpactData: null,
      isActionLoading: false,
      snackbar: { open: false, message: "", severity: "success" },
    };
  }

  componentDidMount() {
    this.loadResellers();
  }

  didGetResellers = (response: List<IResellerSearchResult>) => {
    this.setState({
      isLoading: false,
      resellers: response.data,
      filters: {
        ...this.state.filters,
        page: response.page,
        total: response.total,
      },
    });
  };

  genericError = (message: string) => {
    this.setState({
      isLoading: false,
      isActionLoading: false,
      snackbar: { open: true, message, severity: "error" },
    });
  };

  loadResellers = () => {
    this.setState({ isLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    const { page, limit, filter } = this.state.filters;
    this.controller.searchResellers(token, { page, limit, filter });
  };

  handleSearch = (filter: string) =>
    this.setState(
      (prev) => ({ filters: { ...prev.filters, filter, page: 1 } }),
      this.loadResellers
    );

  handleFilterChange = (newFilter: string) =>
    this.setState((prev) => ({
      filters: { ...prev.filters, filter: newFilter },
    }));

  handlePageChange = (newPage: number) =>
    this.setState(
      (prev) => {
        const totalPages =
          calculateTotalPages(prev.filters.total, prev.filters.limit) || 1;
        const page = Math.min(Math.max(newPage, 1), totalPages);
        if (page === prev.filters.page) {
          return null;
        }
        return { filters: { ...prev.filters, page } };
      },
      this.loadResellers
    );

  handleSnackbarClose = () =>
    this.setState((prev) => ({
      snackbar: { ...prev.snackbar, open: false },
    }));

  handleOpenDemoteModal = (uid: string, name: string) => {
    this.setState({ isActionLoading: true, userToDemote: { uid, name } });
    const token = localStorage.getItem("AccessToken")!;
    this.answeringClient
      .getDemotionImpact(token, uid)
      .then((response) =>
        this.setState({
          isDemoteModalOpen: true,
          isActionLoading: false,
          demotionImpactData: response.data,
        })
      )
      .catch((error) => {
        const msg =
          error?.response?.data?.message ||
          "Erro ao buscar dados do revendedor.";
        this.genericError(msg);
      });
  };

  onCloseDemotion = () =>
    this.setState({
      isDemoteModalOpen: false,
      userToDemote: undefined,
      demotionImpactData: null,
    });

  onConfirmDemotion = () => {
    this.setState({ isActionLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    const uid = this.state.userToDemote!.uid;

    this.answeringClient
      .demoteReseller(token, uid)
      .then(() =>
        this.setState(
          {
            isActionLoading: false,
            isDemoteModalOpen: false,
            snackbar: {
              open: true,
              message: "Cargo de revendedor removido com sucesso!",
              severity: "success",
            },
          },
          this.loadResellers
        )
      )
      .catch((error) => {
        const msg =
          error?.response?.data?.message ||
          "Falha ao remover o cargo do revendedor.";
        this.genericError(msg);
      });
  };

  render() {
    const {
      isLoading,
      resellers,
      filters,
      isDemoteModalOpen,
      isActionLoading,
      userToDemote,
      demotionImpactData,
      snackbar,
    } = this.state;

    return (
      <Box>
        <Box flex={1} overflow="auto">
          <Container maxWidth="lg"
            style={{
              padding: "40px 20px",
            }}
          >

            <Stack direction={"column"} spacing={1.25} marginBottom={2}>
              <Box display="flex" alignItems="center" gap={1} flexWrap={'wrap'}>
                <Typography variant="h2">
                  Usuários Revendedores
                </Typography>

                <Chip
                  label={`${this.state.filters?.total} ${this.state.filters?.total !== 1 ? "encontrados" : "encontrado"}`}
                  size="small"
                />
              </Box>

              <Typography variant="subtitle1" color={'text.secondary'}>
                Controle e visualize a participação dos revendedores
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'start',
                  md: 'end'
                },
                flexWrap: 'wrap'
              }}
            >
              <SearchInput
                label="Pesquisar revendedor..."
                onChangeFilter={this.handleFilterChange}
                onSearch={this.handleSearch}
              />
            </Box>


            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome do Revendedor</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Nº de Clientes</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}

                  {!isLoading && resellers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <EmptyState
                          icon={NoAccounts}
                          title="Nenhum Revendedor Encontrado"
                          description="Tente ajustar os filtros ou adicionar novos revendedores."
                        />
                      </TableCell>
                    </TableRow>
                  )}

                  {!isLoading &&
                    resellers.map((r) => (
                      <ResellerRow
                        key={r.uid}
                        reseller={r}
                        onDemoteClick={this.handleOpenDemoteModal}
                      />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              currentPage={filters.page}
              totalPages={calculateTotalPages(filters.total, filters.limit)}
              onChangePage={this.handlePageChange}
            />
          </Container>
        </Box>

        {isDemoteModalOpen && (
          <DemoteResellerModal
            isOpen={isDemoteModalOpen}
            isLoading={isActionLoading}
            userName={userToDemote?.name ?? ""}
            impactData={demotionImpactData}
            delegate={this}
          />
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.handleSnackbarClose}
        >
          <Alert
            onClose={this.handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default withNavigationHooks(ResellersTab);
